/**
 * Merge following-live status into the stories carousel (mobile story feed parity).
 * Live entities appear first (after "my story"), sorted by viewerCount desc.
 */

export function getStoryEntityId(story) {
  if (!story) return null
  if (story.type === 'page' || (story.page?.id && !story.user?.id)) {
    return story.page?.id || null
  }
  if (story.type === 'user' || story.user?.id) {
    return story.user?.id || null
  }
  return story.page?.id || null
}

export function isPageStoryEntity(story) {
  return story?.type === 'page' || !!(story?.page?.id && !story?.user?.id)
}

export function isMyStoryEntry(story) {
  return String(story?.id || '').startsWith('my-story-')
}

export function normalizeFollowingLiveItems(response) {
  const payload = response?.data ?? response
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.items)) return payload.items
  if (Array.isArray(payload?.data?.items)) return payload.data.items
  return []
}

export function buildViewerCountMap(followingLiveItems = []) {
  const map = {}
  for (const item of followingLiveItems) {
    const entityId = item.pageId || item.userId
    if (entityId) map[entityId] = item.viewerCount ?? 0
  }
  return map
}

export function shouldNavigateToLive(story) {
  return !!(story?.isLive && story?.liveStreamId)
}

export function getStoryDisplayName(story) {
  if (!story) return ''
  if (story.type === 'page') {
    return story.page?.name || story.page?.username || ''
  }
  const user = story.user
  if (!user) return ''
  const first = (user.firstName || '').trim()
  const last = (user.lastName || '').trim()
  if (first || last) return `${first} ${last}`.trim()
  return user.username || ''
}

export function applyLiveStatusToStories(stories, liveUserMap = {}) {
  if (!Array.isArray(stories) || !stories.length) return stories || []
  return stories.map((story) => {
    const entityId = getStoryEntityId(story)
    const liveStreamId = entityId ? liveUserMap[entityId] : null
    const isLive = !!liveStreamId
    if (story.isLive === isLive && story.liveStreamId === (liveStreamId || null)) {
      if (story.isLiveOnly) return { ...story, isLiveOnly: false }
      return story
    }
    return { ...story, isLive, liveStreamId: liveStreamId || null, isLiveOnly: false }
  })
}

export function buildLiveOnlyStoryEntries(followingLiveItems = [], existingEntityIds = new Set()) {
  const entries = []
  const seen = new Set(existingEntityIds)
  for (const item of followingLiveItems) {
    const entityId = item.pageId || item.userId
    if (!entityId || !item.liveStreamId || seen.has(entityId)) continue
    seen.add(entityId)

    if (item.pageId) {
      entries.push({
        id: `live-only-page-${entityId}`,
        type: 'page',
        page: {
          id: item.pageId,
          username: item.username || item.displayName,
          name: item.displayName,
          profilePictureUrl: item.avatarUrl,
        },
        isLive: true,
        liveStreamId: item.liveStreamId,
        isLiveOnly: true,
        allStoriesViewed: true,
        firstUnseenIndex: -1,
        stories: [],
      })
    } else {
      entries.push({
        id: `live-only-user-${entityId}`,
        type: 'user',
        user: {
          id: item.userId,
          username: item.username || item.displayName,
          firstName: item.displayName,
          lastName: '',
          profilePictureUrl: item.avatarUrl,
        },
        isLive: true,
        liveStreamId: item.liveStreamId,
        isLiveOnly: true,
        allStoriesViewed: true,
        firstUnseenIndex: -1,
        stories: [],
      })
    }
  }
  return entries
}

/**
 * Sort friends' entities: live first (viewerCount desc), then original story order.
 */
export function sortStoriesLiveFirst(stories, viewerCountMap = {}) {
  const indexed = stories.map((story, index) => ({ story, index }))
  indexed.sort((a, b) => {
    const aLive = a.story.isLive ? 1 : 0
    const bLive = b.story.isLive ? 1 : 0
    if (aLive !== bLive) return bLive - aLive
    if (aLive && bLive) {
      const aCount = viewerCountMap[getStoryEntityId(a.story)] ?? 0
      const bCount = viewerCountMap[getStoryEntityId(b.story)] ?? 0
      if (bCount !== aCount) return bCount - aCount
    }
    return a.index - b.index
  })
  return indexed.map(({ story }) => story)
}

/**
 * Apply live badges, insert live-only bubbles, sort live-first (mobile parity).
 */
export function mergeStoriesWithLiveFeed(stories, followingLiveItems = [], liveUserMap = {}) {
  const base = Array.isArray(stories) ? stories : []
  const withLive = applyLiveStatusToStories(base, liveUserMap)
  const existingIds = new Set(withLive.map(getStoryEntityId).filter(Boolean))
  const liveOnly = buildLiveOnlyStoryEntries(followingLiveItems, existingIds)
  const viewerCountMap = buildViewerCountMap(followingLiveItems)

  const myStory = withLive.find(isMyStoryEntry) || null
  const rest = withLive.filter((s) => !isMyStoryEntry(s))
  const merged = sortStoriesLiveFirst([...rest, ...liveOnly], viewerCountMap)

  return myStory ? [myStory, ...merged] : merged
}
